/* ==========================================================================
   3two1® – Verhalten (v18)
   Einblenden, Anker, Hero-Video, Galerie & Lightbox, Kaufen per AJAX mit
   Horizon-Warenkorb, Sticky-Kaufleiste, Lieferdatum, Details-Explorer,
   Rechtstext-Pop-ups mit Deeplinks, Kontaktformular, Cookie-Einstellungen
   und der 3two1 Concierge.
   Alles ist auf Wiederholung ausgelegt (Theme-Editor lädt Sections neu).
   ========================================================================== */

const root = document.documentElement;
const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const data = (() => {
  try {
    return JSON.parse(document.getElementById('t1-data')?.textContent || '{}');
  } catch (e) {
    return {};
  }
})();

/* ── Einblenden ------------------------------------------------------------ */
const revealObserver =
  'IntersectionObserver' in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              revealObserver.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

function initReveal(scope = document) {
  scope.querySelectorAll('.t1-reveal:not(.is-visible)').forEach((el) => {
    if (!revealObserver || reduceMotion) el.classList.add('is-visible');
    else revealObserver.observe(el);
  });
}

/* ── Anker mit sanftem Scrollen -------------------------------------------- */
function headerOffset() {
  const h = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height'), 10);
  return (Number.isFinite(h) ? h : 64) + 8;
}
function scrollToEl(el) {
  const top = el.getBoundingClientRect().top + window.scrollY - headerOffset();
  window.scrollTo({ top, behavior: reduceMotion ? 'auto' : 'smooth' });
}
document.addEventListener('click', (event) => {
  const link = event.target.closest('a[data-t1-scroll]');
  if (!link) return;
  const href = link.getAttribute('href') || '';
  if (!href.startsWith('#') || href.length < 2) return;
  const target = document.querySelector(href);
  if (!target) return;
  event.preventDefault();
  scrollToEl(target);
});

/* ── Hero ------------------------------------------------------------------- */
function initHero(scope = document) {
  scope.querySelectorAll('[data-t1-hero]').forEach((hero) => {
    requestAnimationFrame(() => hero.classList.add('is-ready'));
    const videos = hero.querySelectorAll('video');
    const toggle = hero.querySelector('[data-t1-video-toggle]');
    if (reduceMotion) videos.forEach((v) => { v.removeAttribute('autoplay'); v.pause(); });
    if (!toggle || toggle.dataset.bound) return;
    toggle.dataset.bound = '1';
    const sync = () => {
      const paused = [...videos].every((v) => v.paused);
      toggle.classList.toggle('is-paused', paused);
      toggle.setAttribute('aria-label', paused ? 'Video abspielen' : 'Video pausieren');
    };
    toggle.addEventListener('click', () => {
      const anyPlaying = [...videos].some((v) => !v.paused);
      videos.forEach((v) => (anyPlaying ? v.pause() : v.play().catch(() => {})));
      setTimeout(sync, 50);
    });
    sync();
  });
}

/* ── Galerie & Lightbox --------------------------------------------------- */
const lightbox = (() => {
  let el = null;
  let images = [];
  let index = 0;
  let lastFocus = null;
  function build() {
    el = document.createElement('div');
    el.className = 't1-lightbox';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Bildansicht');
    el.innerHTML = `
      <div class="t1-lightbox__stage"><img alt=""></div>
      <button type="button" class="t1-lightbox__btn t1-lightbox__close" aria-label="Schließen"><svg class="t1-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M6 6l12 12M18 6 6 18"/></svg></button>
      <button type="button" class="t1-lightbox__btn t1-lightbox__prev" aria-label="Vorheriges Bild"><svg class="t1-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M19.5 12h-15M10 6.5 4.5 12l5.5 5.5"/></svg></button>
      <button type="button" class="t1-lightbox__btn t1-lightbox__next" aria-label="Nächstes Bild"><svg class="t1-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M4.5 12h15M14 6.5l5.5 5.5-5.5 5.5"/></svg></button>
      <p class="t1-lightbox__count" aria-live="polite"></p>`;
    document.body.appendChild(el);
    el.querySelector('.t1-lightbox__close').addEventListener('click', close);
    el.querySelector('.t1-lightbox__prev').addEventListener('click', () => show(index - 1));
    el.querySelector('.t1-lightbox__next').addEventListener('click', () => show(index + 1));
    el.addEventListener('click', (e) => { if (e.target.classList.contains('t1-lightbox__stage')) close(); });
    let startX = null;
    el.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
    el.addEventListener('touchend', (e) => {
      if (startX === null) return;
      const dx = e.changedTouches[0].clientX - startX;
      if (Math.abs(dx) > 50) show(index + (dx < 0 ? 1 : -1));
      startX = null;
    });
  }
  function show(i) {
    index = (i + images.length) % images.length;
    const img = el.querySelector('img');
    img.src = images[index].src;
    img.alt = images[index].alt || '';
    el.querySelector('.t1-lightbox__count').textContent = `${String(index + 1).padStart(2, '0')} / ${String(images.length).padStart(2, '0')}`;
    const multi = images.length > 1;
    el.querySelector('.t1-lightbox__prev').hidden = !multi;
    el.querySelector('.t1-lightbox__next').hidden = !multi;
  }
  function open(list, start) {
    if (!el) build();
    images = list;
    lastFocus = document.activeElement;
    show(start);
    el.classList.add('is-open');
    root.classList.add('t1-lock');
    el.querySelector('.t1-lightbox__close').focus();
  }
  function close() {
    if (!el) return;
    el.classList.remove('is-open');
    if (!document.querySelector('.t1-modal.is-open')) root.classList.remove('t1-lock');
    lastFocus?.focus?.();
  }
  document.addEventListener('keydown', (e) => {
    if (!el || !el.classList.contains('is-open')) return;
    if (e.key === 'Escape') close();
    if (e.key === 'ArrowRight') show(index + 1);
    if (e.key === 'ArrowLeft') show(index - 1);
  });
  return { open };
})();

function initGallery(scope = document) {
  scope.querySelectorAll('[data-t1-gallery]').forEach((gallery) => {
    if (gallery.dataset.bound) return;
    gallery.dataset.bound = '1';
    const track = gallery.querySelector('[data-t1-gallery-track]');
    const slides = [...gallery.querySelectorAll('[data-t1-slide]')];
    const thumbs = [...gallery.querySelectorAll('[data-t1-thumb]')];
    const count = gallery.querySelector('[data-t1-gallery-count]');
    const progress = gallery.querySelector('[data-t1-gallery-progress]');
    if (!track || !slides.length) return;
    const pad = (n) => String(n).padStart(2, '0');
    let current = 0;

    const setActive = (i) => {
      current = i;
      if (count) count.textContent = `${pad(i + 1)} / ${pad(slides.length)}`;
      if (progress) progress.style.setProperty('--t1-progress', `${((i + 1) / slides.length) * 100}%`);
      thumbs.forEach((t, n) => t.classList.toggle('is-active', n === i));
      const activeThumb = thumbs[i];
      if (activeThumb && activeThumb.parentElement.scrollHeight > activeThumb.parentElement.clientHeight) {
        activeThumb.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    };
    const goTo = (i, wrap = false) => {
      let n = Math.max(0, Math.min(slides.length - 1, i));
      if (wrap && i >= slides.length) n = 0;
      const jump = reduceMotion || (wrap && n === 0);
      track.scrollTo({ left: slides[n].offsetLeft - track.offsetLeft, behavior: jump ? 'auto' : 'smooth' });
      setActive(n);
    };
    let ticking = false;
    track.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const i = Math.round(track.scrollLeft / track.clientWidth);
        if (i !== current) setActive(Math.max(0, Math.min(slides.length - 1, i)));
        ticking = false;
      });
    }, { passive: true });
    /* Diashow: läuft nur, wenn sichtbar und nicht angehalten. Jede Berührung, jeder Klick
       auf Pfeile/Miniaturen und das Vergrößern hält sie an; Maus darüber pausiert kurz. */
    const interval = Number(gallery.dataset.autoplay || 0);
    const playBtn = gallery.querySelector('[data-t1-gallery-play]');
    let timer = null;
    let userPaused = reduceMotion;
    let hoverPaused = false;
    let inView = false;
    const canRun = () => interval > 0 && slides.length > 1 && !userPaused && !hoverPaused && inView && !document.hidden;
    const schedule = () => {
      clearTimeout(timer);
      if (!canRun()) return;
      timer = setTimeout(() => {
        goTo(current + 1, true);
        schedule();
      }, interval);
    };
    const setUserPaused = (paused) => {
      userPaused = paused;
      if (playBtn) {
        playBtn.classList.toggle('is-paused', paused);
        playBtn.setAttribute('aria-label', paused ? 'Diashow abspielen' : 'Diashow anhalten');
        playBtn.setAttribute('aria-pressed', paused ? 'true' : 'false');
      }
      schedule();
    };
    if (interval > 0) {
      setUserPaused(userPaused);
      playBtn?.addEventListener('click', () => setUserPaused(!userPaused));
      gallery.addEventListener('mouseenter', () => { hoverPaused = true; schedule(); });
      gallery.addEventListener('mouseleave', () => { hoverPaused = false; schedule(); });
      gallery.addEventListener('focusin', (e) => { if (e.target !== playBtn) { hoverPaused = true; schedule(); } });
      gallery.addEventListener('focusout', () => { hoverPaused = false; schedule(); });
      track.addEventListener('touchstart', () => setUserPaused(true), { passive: true });
      track.addEventListener('wheel', (e) => { if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) setUserPaused(true); }, { passive: true });
      document.addEventListener('visibilitychange', schedule);
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(([entry]) => { inView = entry.isIntersecting; schedule(); }, { threshold: 0.45 }).observe(track);
      } else {
        inView = true;
        schedule();
      }
    }
    const manual = (fn) => () => { if (interval > 0) setUserPaused(true); fn(); };

    gallery.querySelector('[data-t1-gallery-prev]')?.addEventListener('click', manual(() => goTo(current - 1)));
    gallery.querySelector('[data-t1-gallery-next]')?.addEventListener('click', manual(() => goTo(current + 1)));
    thumbs.forEach((t) => t.addEventListener('click', manual(() => goTo(Number(t.dataset.t1Thumb)))));
    track.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { e.preventDefault(); manual(() => goTo(current + 1))(); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); manual(() => goTo(current - 1))(); }
    });

    const zoomButtons = [...gallery.querySelectorAll('[data-t1-zoom]')];
    const list = zoomButtons.map((b) => ({ src: b.dataset.src, alt: b.parentElement.querySelector('img')?.alt || '' }));
    zoomButtons.forEach((b, n) => b.addEventListener('click', manual(() => lightbox.open(list, n))));
  });
}

/* ── Kaufen per AJAX (öffnet den Horizon-Warenkorb) ---------------------------- */
let themeEvents = null;
/* Horizon legt `Theme` als globales const an (nicht an window) – daher so auslesen. */
function themeRoutes() {
  let t;
  try { t = Theme; } catch (e) { t = window.Theme; } // eslint-disable-line no-undef
  const r = (t && t.routes) || {};
  const addUrl = (r.cart_add_url || '/cart/add.js');
  return {
    add: addUrl.endsWith('.js') ? addUrl : `${addUrl}.js`,
    cart: r.cart_url || '/cart',
  };
}
import('@theme/events')
  .then((mod) => { themeEvents = mod; })
  .catch(() => { themeEvents = null; });

async function addToCart(form) {
  const button = form.querySelector('[data-t1-add]');
  const error = form.querySelector('[data-t1-error]');
  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((c) => c.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));
  formData.append('sections_url', window.location.pathname);

  button?.classList.add('is-loading');
  if (error) error.hidden = true;
  try {
    const routes = themeRoutes();
    const response = await fetch(routes.add, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const result = await response.json();
    if (!response.ok || result.status) {
      throw new Error(result.description || result.message || 'Die Tasche konnte nicht in den Warenkorb gelegt werden.');
    }
    const cart = await fetch(`${routes.cart}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
    if (themeEvents?.CartAddEvent) {
      document.dispatchEvent(
        new themeEvents.CartAddEvent(cart, String(formData.get('id')), {
          source: '3two1',
          itemCount: cart.item_count,
          productId: form.closest('[data-product-id]')?.dataset.productId,
          sections: result.sections,
        })
      );
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer && !drawer.hasAttribute('auto-open') && typeof drawer.open === 'function') drawer.open();
      if (!drawer) window.location.href = routes.cart;
    } else {
      window.location.href = routes.cart;
    }
  } catch (err) {
    if (error) {
      error.textContent = err.message;
      error.hidden = false;
    }
  } finally {
    button?.classList.remove('is-loading');
  }
}

function initProductForms(scope = document) {
  scope.querySelectorAll('form[data-t1-product-form]').forEach((form) => {
    if (form.dataset.bound) return;
    form.dataset.bound = '1';
    form.addEventListener('submit', (event) => {
      if (event.submitter && event.submitter.closest('.shopify-payment-button')) return;
      event.preventDefault();
      addToCart(form);
    });
  });
}

/* ── Sticky-Kaufleiste ------------------------------------------------------------ */
function initStickyBar(scope = document) {
  const bar = scope.querySelector('[data-t1-stickybar]') || document.querySelector('[data-t1-stickybar]');
  if (!bar || bar.dataset.bound || !('IntersectionObserver' in window)) return;
  bar.dataset.bound = '1';
  const product = document.querySelector('[data-t1-product]');
  const addButton = product?.querySelector('[data-t1-add]');
  const footer = document.getElementById('t1-footer');
  const stickyButton = bar.querySelector('[data-t1-sticky-add]');
  if (!product || !addButton) return;

  let buttonAbove = false;
  let footerVisible = false;
  const update = () => {
    const show = buttonAbove && !footerVisible;
    bar.classList.toggle('is-visible', show);
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    stickyButton.tabIndex = show ? 0 : -1;
    root.style.setProperty('--t1-bottom-offset', show ? `${bar.offsetHeight}px` : '0px');
  };
  new IntersectionObserver(([entry]) => {
    buttonAbove = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    update();
  }).observe(addButton);
  if (footer) {
    new IntersectionObserver(([entry]) => {
      footerVisible = entry.isIntersecting;
      update();
    }).observe(footer);
  }
  stickyButton.addEventListener('click', () => {
    const form = document.getElementById(stickyButton.dataset.form);
    if (form) addToCart(form);
  });
}

/* ── Voraussichtliches Lieferdatum ---------------------------------------------- */
function easterSunday(year) {
  const a = year % 19, b = Math.floor(year / 100), c = year % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}
function germanHolidays(year) {
  const e = easterSunday(year);
  const plus = (days) => new Date(e.getFullYear(), e.getMonth(), e.getDate() + days);
  return [
    new Date(year, 0, 1), plus(-2), plus(1), new Date(year, 4, 1), plus(39), plus(50),
    new Date(year, 9, 3), new Date(year, 11, 25), new Date(year, 11, 26),
  ].map((d) => d.toDateString());
}
function addBusinessDays(start, days) {
  const d = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  let added = 0;
  while (added < days) {
    d.setDate(d.getDate() + 1);
    const holidays = germanHolidays(d.getFullYear());
    if (d.getDay() !== 0 && d.getDay() !== 6 && !holidays.includes(d.toDateString())) added += 1;
  }
  return d;
}
function initDelivery(scope = document) {
  const fmt = new Intl.DateTimeFormat('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' });
  scope.querySelectorAll('[data-t1-delivery]').forEach((el) => {
    const min = Number(el.dataset.min || data.deliveryMin || 2);
    const max = Number(el.dataset.max || data.deliveryMax || 3);
    const text = el.querySelector('[data-t1-delivery-text]');
    if (!text) return;
    const now = new Date();
    const from = fmt.format(addBusinessDays(now, min));
    const to = fmt.format(addBusinessDays(now, max));
    text.innerHTML = `Versandkostenfrei mit DHL – voraussichtlich bei dir: <strong>${from === to ? from : `${from} – ${to}`}</strong>`;
  });
}

/* ── Zeitstrahl ------------------------------------------------------------------ */
function initTimeline(scope = document) {
  scope.querySelectorAll('[data-t1-timeline]').forEach((tl) => {
    if (tl.dataset.bound) return;
    tl.dataset.bound = '1';
    if (reduceMotion || !('IntersectionObserver' in window)) { tl.classList.add('is-drawn'); return; }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { tl.classList.add('is-drawn'); io.disconnect(); }
    }, { threshold: 0.35 });
    io.observe(tl);
  });
}

/* ── Details-Explorer -------------------------------------------------------------- */
function initFeatures(scope = document) {
  scope.querySelectorAll('[data-t1-features]').forEach((section) => {
    if (section.dataset.bound) return;
    section.dataset.bound = '1';
    const items = [...section.querySelectorAll('[data-t1-feature]')];
    const frames = [...section.querySelectorAll('[data-t1-frame]')];
    const activate = (i) => {
      items.forEach((item, n) => {
        const on = n === i;
        item.classList.toggle('is-active', on);
        item.querySelector('.t1-feature__btn')?.setAttribute('aria-expanded', on ? 'true' : 'false');
      });
      frames.forEach((f, n) => f.classList.toggle('is-active', n === i));
    };
    items.forEach((item, i) => {
      item.querySelector('.t1-feature__btn')?.addEventListener('click', () => {
        const isOpen = item.classList.contains('is-active');
        if (isOpen && window.matchMedia('(max-width: 989px)').matches) {
          item.classList.remove('is-active');
          item.querySelector('.t1-feature__btn')?.setAttribute('aria-expanded', 'false');
          return;
        }
        activate(i);
      });
      item.addEventListener('shopify:block:select', () => activate(i));
    });
  });
}

/* ── Pop-ups: Rechtstexte & Kontakt ------------------------------------------------- */
let lastTrigger = null;
function openModal(modal, trigger) {
  if (!modal) return;
  document.querySelectorAll('.t1-modal.is-open').forEach((m) => m !== modal && m.classList.remove('is-open'));
  lastTrigger = trigger || document.activeElement;
  modal.classList.add('is-open');
  root.classList.add('t1-lock');
  const card = modal.querySelector('.t1-modal__card');
  if (card) card.scrollTop = 0;
  requestAnimationFrame(() => modal.querySelector('.t1-modal__close')?.focus());
  document.querySelector('[data-t1-concierge]')?.classList.remove('is-open');
}
function closeModal(modal) {
  if (!modal) return;
  modal.classList.remove('is-open');
  if (location.hash && modal.id && location.hash.slice(1) === modal.id) {
    history.replaceState(null, '', location.pathname + location.search);
  }
  if (!document.querySelector('.t1-modal.is-open')) root.classList.remove('t1-lock');
  lastTrigger?.focus?.();
}
document.addEventListener('click', (event) => {
  const legal = event.target.closest('[data-t1-open-legal]');
  if (legal) {
    const modal = document.querySelector(`[data-t1-legal-modal="${legal.dataset.t1OpenLegal}"]`);
    if (modal) { event.preventDefault(); openModal(modal, legal); }
    return;
  }
  const contact = event.target.closest('[data-t1-open-contact]');
  if (contact) {
    const modal = document.querySelector('[data-t1-contact-modal]');
    if (modal) { event.preventDefault(); openModal(modal, contact); }
    return;
  }
  const close = event.target.closest('[data-t1-close]');
  if (close) {
    event.preventDefault();
    closeModal(close.closest('.t1-modal'));
    return;
  }
  if (event.target.classList?.contains('t1-modal')) closeModal(event.target);
});
document.addEventListener('keydown', (event) => {
  const open = document.querySelector('.t1-modal.is-open');
  if (!open) return;
  if (event.key === 'Escape') closeModal(open);
  if (event.key === 'Tab') {
    const focusable = [...open.querySelectorAll('a[href], button:not([disabled]), input, textarea, select')].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }
});
/* Rechtstexte, die als Shopify-Richtlinie hinterlegt sind, haben kein Pop-up mehr:
   alte Deeplinks (#agb, ?t1_legal=widerruf …) leiten auf /policies/… weiter. */
const legalUrls = data.legalUrls || {};
function policyRedirect(doc) {
  const url = Object.prototype.hasOwnProperty.call(legalUrls, doc) ? legalUrls[doc] : '';
  if (!url || document.getElementById(doc)) return false;
  location.replace(url);
  return true;
}
function openFromUrl() {
  const params = new URLSearchParams(location.search);
  const wanted = params.get('t1_legal') || location.hash.slice(1);
  if (wanted) {
    if (policyRedirect(wanted)) return;
    const modal = document.getElementById(wanted);
    if (modal?.classList.contains('t1-modal')) openModal(modal);
  }
  if (params.get('contact_posted') === 'true' || document.querySelector('#t1-contact-form .t1-form__errors')) {
    openModal(document.querySelector('[data-t1-contact-modal]'));
  }
}
window.addEventListener('hashchange', () => {
  if (location.hash && policyRedirect(location.hash.slice(1))) return;
  const modal = location.hash ? document.getElementById(location.hash.slice(1)) : null;
  if (modal?.classList.contains('t1-modal')) openModal(modal);
});

/* ── Instagram: Live-Feed erst nach Zustimmung (Zwei-Klick) -------------------------- */
function marketingAllowed() {
  try { return Boolean(window.Shopify?.customerPrivacy?.marketingAllowed?.()); } catch (e) { return false; }
}
function initConsentGates(scope = document) {
  scope.querySelectorAll('[data-t1-consent-gate]').forEach((gate) => {
    if (gate.dataset.bound) return;
    gate.dataset.bound = '1';
    const load = () => {
      if (gate.dataset.loaded) return;
      gate.dataset.loaded = '1';
      const tpl = gate.querySelector('[data-t1-consent-template]');
      gate.querySelector('[data-t1-consent-placeholder]')?.remove();
      if (!tpl) return;
      const frag = tpl.content.cloneNode(true);
      // Skripte im Template sind inaktiv – als neue Elemente einsetzen, damit sie laufen
      frag.querySelectorAll('script').forEach((old) => {
        const s = document.createElement('script');
        [...old.attributes].forEach((a) => s.setAttribute(a.name, a.value));
        s.textContent = old.textContent;
        old.replaceWith(s);
      });
      gate.appendChild(frag);
    };
    gate.querySelector('[data-t1-consent-load]')?.addEventListener('click', load);
    if (gate.hasAttribute('data-t1-consent-open') || window.Shopify?.designMode || marketingAllowed()) load();
    document.addEventListener('visitorConsentCollected', () => { if (marketingAllowed()) load(); });
  });
}

/* ── Cookie-Einstellungen (Shopify Customer Privacy) ------------------------------- */
function initCookiePrefs() {
  const buttons = document.querySelectorAll('[data-t1-cookie-prefs]');
  if (!buttons.length) return;
  const check = () => {
    if (window.privacyBanner?.showPreferences) {
      buttons.forEach((b) => {
        b.hidden = false;
        if (!b.dataset.bound) {
          b.dataset.bound = '1';
          b.addEventListener('click', () => window.privacyBanner.showPreferences());
        }
      });
      return true;
    }
    return false;
  };
  if (!check()) window.addEventListener('load', () => setTimeout(check, 1200), { once: true });
}

/* ── 3two1 Concierge (regelbasierte Sofortantworten) --------------------------------- */
const price = data.price || '49,99 €';
const email = data.email || 'info@3two1.de';
const minD = data.deliveryMin || 2;
const maxD = data.deliveryMax || 3;
const weight = data.weight || 'unter 1 kg';
const legalLink = (doc, label) => (legalUrls[doc]
  ? `<a href="${legalUrls[doc]}">${label}</a>`
  : `<a href="#${doc}" data-t1-open-legal="${doc}">${label}</a>`);
const KB = [
  { k: ['preis', 'kostet', 'kosten', 'teuer', 'euro', '€'], a: `Die Founders Gym Bag kostet ${price} – Endpreis, versandkostenfrei innerhalb Deutschlands. Gemäß § 19 UStG wird keine Umsatzsteuer ausgewiesen.` },
  { k: ['versand', 'lieferung', 'lieferzeit', 'dhl', 'wann kommt', 'liefern', 'ausland', 'österreich', 'schweiz'], a: `Wir liefern aktuell ausschließlich innerhalb Deutschlands mit DHL – versandkostenfrei. Die Lieferzeit beträgt in der Regel ${minD}–${maxD} Werktage. ${legalLink('versand', 'Versand & Lieferung')}` },
  { k: ['zahl', 'paypal', 'apple pay', 'klarna', 'kreditkarte', 'bezahlen', 'raten'], a: 'Bezahlen kannst du im sicheren Shopify-Checkout, z. B. mit PayPal, Apple Pay, Klarna oder Kreditkarte.' },
  { k: ['laptop', 'notebook', 'macbook', 'tablet'], a: 'Ja – im 35-Liter-Hauptfach gibt es ein eigenes Laptopfach sowie eine Innentasche mit Reißverschluss.' },
  { k: ['maße', 'masse', 'groß', 'gross', 'größe', 'groesse', 'abmessung', 'liter', 'volumen'], a: `Die Tasche misst 55 × 26 × 33 cm, das Hauptfach fasst 35 Liter. Empfohlene Traglast: bis 10 kg.` },
  { k: ['handgepäck', 'handgepaeck', 'flugzeug', 'airline', 'kabine'], a: 'Ob sie als Handgepäck mitdarf, entscheidet die Airline: Die Tasche misst 55 × 26 × 33 cm. Bitte prüfe vor dem Flug die aktuellen Handgepäckmaße deiner Fluggesellschaft.' },
  { k: ['reise', 'reisen', 'urlaub', 'weekender', 'wochenende', 'trip', 'koffer'], a: 'Ja – mit 35 Litern Hauptfach ist sie ein Weekender für Kurztrips. Schuhe und nasse Sachen bleiben getrennt, mit den Rückengurten hast du im Terminal die Hände frei, und leer lässt sie sich kompakt zusammenfalten.' },
  { k: ['falt', 'zusammenleg', 'verstauen', 'platzsparend', 'kompakt'], a: 'Ja, leer lässt sich die Founders Gym Bag kompakt zusammenfalten – praktisch im Koffer, im Schrank oder als Zusatztasche für den Rückweg.' },
  { k: ['schuh', 'sneaker'], a: 'Ja, es gibt ein separates Schuhfach – so bleiben Sneaker und Kleidung sauber getrennt.' },
  { k: ['nass', 'wasser', 'regen', 'dicht', 'handtuch'], a: 'Das Material ist wasserabweisend, aber nicht wasserdicht. Für nasse Kleidung oder Handtücher gibt es ein Nassfach mit wasserabweisendem Innenfutter. Feuchte Tasche nach Gebrauch bitte offen trocknen lassen.' },
  { k: ['trag', 'rucksack', 'schulter', 'hand', 'fahrrad', 'rücken', 'ruecken'], a: 'Drei Tragearten: in der Hand über die Tragegriffe, über der Schulter mit dem Schultergurt oder auf dem Rücken mit den versteckten Rückengurten.' },
  { k: ['material', 'polyester', 'stoff'], a: 'Textile Bestandteile: 100 % Polyester. Nichttextile Bestandteile wie Reißverschlüsse und Beschläge können Metall und Kunststoff enthalten.' },
  { k: ['farbe', 'schwarz', 'farben'], a: 'Die Founders Gym Bag gibt es aktuell in Schwarz.' },
  { k: ['rückgabe', 'rueckgabe', 'widerruf', 'retoure', 'zurück', 'umtausch'], a: `Du hast ein 14-tägiges Widerrufsrecht. Die unmittelbaren Kosten der Rücksendung trägst du. Alle Details: ${legalLink('widerruf', 'Rückgabe & Widerruf')}` },
  { k: ['garantie', 'gewährleistung', 'gewaehrleistung', 'kaputt', 'defekt', 'mangel'], a: `Es gelten 24 Monate gesetzliche Gewährleistung. Zusätzlich gibt 3two1® freiwillig 1 Jahr Herstellergarantie auf Material- und Verarbeitungsfehler. ${legalLink('garantie', 'Garantiebedingungen')}` },
  { k: ['gewicht', 'schwer', 'kilo', 'leicht', 'traglast'], a: `Die Tasche wiegt ${weight}. Die empfohlene maximale Traglast liegt bei 10 kg.` },
  { k: ['story', 'geschichte', '31', 'name', 'gründer', 'gruender', 'felix', 'florian', 'logo'], a: '3two1® entstand aus der Freundschaft von Felix und Florian: 2017 haben sie sich in Oldenburg kennengelernt – in der Hausnummer 31. Das Logo verbindet die 31 mit zwei ineinandergreifenden „F“.' },
  { k: ['kontakt', 'mail', 'erreichen', 'frage', 'support', 'hilfe'], a: `Schreib uns gern an <a href="mailto:${email}">${email}</a> oder über das <a href="#kontakt" data-t1-open-contact>Kontaktformular</a> – die Antwort kommt von uns persönlich.` },
  { k: ['impressum', 'firma', 'gbr', 'anbieter', 'hersteller'], a: `3two1® ist eine Marke der Hausjell und Rüger GbR, Pappelallee 3, 26209 Hatten. ${legalLink('impressum', 'Impressum')}` },
  { k: ['agb', 'bedingungen'], a: `Unsere AGB findest du hier: ${legalLink('agb', 'AGB')}` },
  { k: ['geschenk', 'verschenken', 'gutschein'], a: `Die Founders Gym Bag ist ein schönes Geschenk. Für besondere Wünsche (z. B. Lieferung an eine andere Adresse) schreib uns gern an <a href="mailto:${email}">${email}</a>.` },
];
const normalize = (s) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ß/g, 'ss');
function answer(question) {
  const q = normalize(question);
  let best = null;
  let bestScore = 0;
  KB.forEach((item) => {
    const score = item.k.reduce((sum, key) => sum + (q.includes(normalize(key)) ? key.length : 0), 0);
    if (score > bestScore) { best = item; bestScore = score; }
  });
  return best
    ? best.a
    : `Dazu habe ich leider keine sichere Antwort. Schreib uns am besten direkt an <a href="mailto:${email}">${email}</a> oder über das <a href="#kontakt" data-t1-open-contact>Kontaktformular</a>.`;
}
function initConcierge() {
  const wrap = document.querySelector('[data-t1-concierge]');
  if (!wrap || wrap.dataset.bound) return;
  wrap.dataset.bound = '1';
  const toggle = wrap.querySelector('[data-t1-concierge-toggle]');
  const body = wrap.querySelector('[data-t1-concierge-body]');
  const form = wrap.querySelector('[data-t1-concierge-form]');
  const input = wrap.querySelector('[data-t1-concierge-input]');
  const setOpen = (open) => {
    wrap.classList.toggle('is-open', open);
    toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) setTimeout(() => input?.focus(), 60);
  };
  const bubble = (html, who) => {
    const div = document.createElement('div');
    div.className = `t1-bubble t1-bubble--${who}`;
    if (who === 'user') div.textContent = html;
    else div.innerHTML = html;
    body.appendChild(div);
    body.scrollTop = body.scrollHeight;
  };
  const ask = (q) => {
    bubble(q, 'user');
    setTimeout(() => bubble(answer(q), 'bot'), 260);
  };
  toggle.addEventListener('click', () => setOpen(!wrap.classList.contains('is-open')));
  wrap.querySelector('[data-t1-concierge-close]')?.addEventListener('click', () => setOpen(false));
  wrap.querySelectorAll('[data-t1-q]').forEach((b) => b.addEventListener('click', () => ask(b.dataset.t1Q)));
  form?.addEventListener('submit', (e) => {
    e.preventDefault();
    const q = input.value.trim();
    if (!q) return;
    input.value = '';
    ask(q);
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') setOpen(false); });
  document.addEventListener('click', (e) => {
    if (wrap.classList.contains('is-open') && !wrap.contains(e.target) && !e.target.closest('.t1-modal')) setOpen(false);
  });
}

/* ── Start & Theme-Editor ------------------------------------------------------------ */
function initAll(scope = document) {
  initReveal(scope);
  initHero(scope);
  initGallery(scope);
  initProductForms(scope);
  initStickyBar(scope);
  initDelivery(scope);
  initFeatures(scope);
  initTimeline(scope);
  initConsentGates(scope);
}
function boot() {
  initAll(document);
  initCookiePrefs();
  initConcierge();
  openFromUrl();
  window.__t1Ready = true;
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
else boot();

document.addEventListener('shopify:section:load', (event) => initAll(event.target));
